import { ChangeDetectionStrategy, Component, inject, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatError, MatFormFieldModule, MatHint, MatLabel } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { Router } from '@angular/router';
import { TranslatePipe } from '@ngx-translate/core';
import md5 from 'md5';
import { Subscription } from 'rxjs';
import { AccountStateService } from '../../../../services/account-state.service';
import { LoadingService } from '../../../../services/loading.service';
import { LocalStorageService } from '../../../../services/local-storage.service';
import { PosthogService } from '../../../../services/posthog.service';
import { RoutingService } from '../../../../services/routing.service';
import { UserAdminService } from '../../../../services/useradmin.service';
import { noOnlyWhitespaceValidator, PATTERN_TEXT } from '../../../../utils/normalize-string';

// eslint-disable-next-line sonarjs/no-hardcoded-passwords -- This is an invalid password hash, used to reset password
const INVALID_PASSWORD_HASH = '4f0cc03bba4a7985a1311cbe02ccd763';

declare const window: Window & {
  google?: {
    accounts?: {
      id?: {
        disableAutoSelect?: () => void;
      };
    };
  };
};

@Component({
  selector: 'passwordchange',
  templateUrl: './passwordchange.component.html',
  styleUrls: ['./passwordchange.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [
    MatLabel,
    MatIconModule,
    MatHint,
    MatError,
    TranslatePipe,
    MatFormFieldModule,
    ReactiveFormsModule,
    MatInputModule,
    MatButtonModule,
  ],
})
export class PasswordChangeComponent implements OnInit {
  public userAdminService = inject(UserAdminService);
  private _accountStateService = inject(AccountStateService);
  private _loadingService = inject(LoadingService);
  private _formBuilder = inject(FormBuilder);
  private _router = inject(Router);
  private _routingService = inject(RoutingService);
  private _posthogService = inject(PosthogService);

  public hide = true;
  public hideConfirm = true;
  public passwordForceChange: boolean;
  private allLoadedSubscriber: Subscription;
  public $isMobile = this._routingService.$isMobile;

  public passwordForm: FormGroup = this._formBuilder.group(
    {
      password: [
        '',
        [
          Validators.required,
          Validators.minLength(6),
          Validators.maxLength(50),
          Validators.pattern(PATTERN_TEXT),
          noOnlyWhitespaceValidator,
        ],
      ],
      passwordConfirm: [
        '',
        [
          Validators.required,
          Validators.minLength(6),
          Validators.maxLength(50),
          Validators.pattern(PATTERN_TEXT),
          noOnlyWhitespaceValidator,
        ],
      ],
    },
    {
      validators: [this.passwordValidation],
    }
  );

  get password() {
    return this.passwordForm.get('password');
  }
  get passwordConfirm() {
    return this.passwordForm.get('passwordConfirm');
  }

  ngOnInit(): void {
    if (this._accountStateService.$account()) {
      this.passwordForceChange = this._accountStateService.$account().password_force_change;
    } else {
      this.allLoadedSubscriber = this._loadingService.allLoadedEvent$.subscribe(() => {
        this.passwordForceChange = this._accountStateService.$account().password_force_change;
        this.allLoadedSubscriber.unsubscribe();
      });
    }
  }

  private passwordValidation(formGroup: FormGroup): object | null {
    const password = formGroup.get('password')?.value;
    const passwordConfirm = formGroup.get('passwordConfirm')?.value;

    if (password !== passwordConfirm) return { passError: true };
    else if (md5(password) === INVALID_PASSWORD_HASH) return { passInvalid: true };
    return null;
  }

  public async onSubmit() {
    await this.userAdminService.sendNewPassword(this.password.value);

    if (this.passwordForceChange) {
      this._router.navigate(['/dashboard']);
    } else {
      this.logout();
    }
  }

  public logout() {
    this._posthogService.captureEvent('logout', { source: 'component', from: 'passwordchange' });
    LocalStorageService.removeElement('accountConnect');
    this._loadingService.logout();
    window.google?.accounts.id.disableAutoSelect();
  }
}
